import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Loader2, AlertTriangle, User, Calendar } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type ServiceReport = {
  id: string;
  reason: string;
  description: string | null;
  created_at: string;
  reporter_id: string;
  profiles: {
    id: string;
    first_name: string | null;
    last_name: string | null;
  } | null;
};

type Service = {
  id: string;
  title: string;
  report_count: number;
};

interface ServiceReportsModalProps {
  service: Service;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ServiceReportsModal: React.FC<ServiceReportsModalProps> = ({
  service,
  open,
  onOpenChange,
}) => {
  const [reports, setReports] = useState<ServiceReport[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchReports = async () => {
      if (!open || !service.id) return;

      try {
        setLoading(true);
        
        // Step 1: Fetch service reports without join
        const { data: reportsData, error: reportsError } = await supabase
          .from("service_reports")
          .select("id, reason, description, created_at, reporter_id")
          .eq("service_id", service.id)
          .order("created_at", { ascending: false });

        if (reportsError) throw reportsError;
        if (!reportsData || reportsData.length === 0) {
          setReports([]);
          return;
        }

        // Step 2: Fetch reporter profiles separately
        const userIds = reportsData.map(r => r.reporter_id);
        const { data: profilesData, error: profilesError } = await supabase
          .from("profiles")
          .select("id, first_name, last_name")
          .in("id", userIds);

        if (profilesError) throw profilesError;

        // Step 3: Merge reports with profiles
        const profilesMap = new Map(profilesData?.map(p => [p.id, p]) || []);
        const merged: ServiceReport[] = reportsData.map(r => ({
          ...r,
          profiles: profilesMap.get(r.reporter_id) || null,
        }));

        setReports(merged);
      } catch (error) {
        console.error("Error fetching reports:", error);
        toast.error("Failed to load service reports");
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [open, service.id]);

  const getReporterName = (profiles: ServiceReport["profiles"]) => {
    if (!profiles) return "Anonymous";
    
    const firstName = profiles.first_name || "";
    const lastName = profiles.last_name || "";
    const fullName = `${firstName} ${lastName}`.trim();
    
    return fullName || "Anonymous";
  };

  const getReasonBadgeVariant = (reason: string) => {
    switch (reason.toLowerCase()) {
      case "inappropriate content":
        return "destructive";
      case "spam":
        return "destructive";
      case "misleading information":
        return "destructive";
      case "poor quality":
        return "secondary";
      case "other":
        return "outline";
      default:
        return "secondary";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            Service Reports: {service.title}
          </DialogTitle>
          <DialogDescription>
            {service.report_count} report{service.report_count !== 1 ? 's' : ''} submitted for this service
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto space-y-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-anyskill-purple" />
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No reports found for this service.
            </div>
          ) : (
            reports.map((report) => (
              <div key={report.id} className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-500" />
                    <span className="font-medium">{getReporterName(report.profiles)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Calendar className="h-4 w-4" />
                    {new Date(report.created_at).toLocaleDateString()} at{" "}
                    {new Date(report.created_at).toLocaleTimeString()}
                  </div>
                </div>
                
                <div className="mb-3">
                  <Badge variant={getReasonBadgeVariant(report.reason)}>
                    {report.reason}
                  </Badge>
                </div>
                
                {report.description && (
                  <div className="bg-white rounded p-3 border">
                    <p className="text-sm text-gray-700">{report.description}</p>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
